/* istanbul ignore file */
import React, { useMemo } from 'react';
import PropTypes, { func } from 'prop-types';
import { mergeClasses } from '@fluentui/react-components';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { CopilotRepositoryProvider } from '@bingads-webui/inline-copilot';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getStyles, useDefaultContainerStyles } from './styles';
import { TextAssetCollectionWithCoPilot } from './text-asset-collection-with-copilot';

const TextAssetCollectionWithRecommendationsInternal = (props) => {
  const {
    url,
    i18n,
    campaignId,
    serverContext,
    shortHeadline,
    onChangeTexts,
    shortHeadlineValidations,
  } = props;
  const classes = useDefaultContainerStyles();
  const helpButtonStyles = useDefaultStyles(getStyles);
  const { CurrentLocale: locale } = serverContext;


  const updatedHeadlineSchema = useMemo(() => Object.keys(AssetGroup.properties.Headlines.schema.properties).reduce((acc, key) => {
    const value = AssetGroup.properties.Headlines.schema.properties[key];
    if (key.includes('Headline')) {
      const updatedValue = {
        ...value,
        minLength: shortHeadlineValidations.minLength || 0,
        maxLength: shortHeadlineValidations.maxLength,
      };
      return {
        ...acc,
        [key]: updatedValue,
      };
    }
    return {
      ...acc,
      [key]: value,
    };
  }, {}), [shortHeadlineValidations.maxLength, shortHeadlineValidations.minLength]);

  const updatedAssetGroup = useMemo(
    () => ({
      ...AssetGroup,
      properties: {
        ...AssetGroup.properties,
        Headline: {
          ...AssetGroup.properties.Headline,
          maxLength: shortHeadlineValidations.maxLength,
        },
        Headlines: {
          ...AssetGroup.properties.Headlines,
          schema: {
            ...AssetGroup.properties.Headlines.schema,
            properties: {
              ...AssetGroup.properties.Headlines.schema.properties,
              ...updatedHeadlineSchema,
            },
          },
        },
      },
    }),
    [
      shortHeadlineValidations.maxLength,
      updatedHeadlineSchema,
    ]
  );

  const handleValidate = ({
    data,
    isDirty,
    isValid,
    messages,
  }) => {
    const {
      Headlines,
      IsOptional,
    } = data;
    const updatedTextAssets = {
      isOptional: IsOptional,
      isDirty,
      isValid,
      messages,
    };
    const newShortHeadline = Headlines ? Headlines.Headline0 || '' : '';
    if (!shortHeadlineValidations.isMultipleOptions &&
      shortHeadline !== newShortHeadline) {
      updatedTextAssets.shortHeadline = newShortHeadline;
    }

    onChangeTexts(updatedTextAssets);
  };

  const handleReset = (key) => {
    const updatedTextAssets = {
      ...(key === 'shortHeadline' ? { shortHeadline: '' } : {}),
    };
    onChangeTexts(updatedTextAssets);
  };

  return (
    <div className={mergeClasses('display-template-text-asset-collection-container', classes.container, helpButtonStyles.collapseContainer)}>
      <ValidationGroup
        validationProps={{
          schema: updatedAssetGroup,
          onValidate: handleValidate,
          data: {
            FinalUrlForRecommendation: url,
            Headlines: {
              Headline0: shortHeadline,
            },
          },
          i18n,
        }}
      >
        <CopilotRepositoryProvider
          finalUrl={url}
          locale={locale}
          enableLogoAndCTA
          campaignId={campaignId}
          recommendationType="DisplayAd"
          serverContext={serverContext}
        >
          <TextAssetCollectionWithCoPilot onResetTextAssets={handleReset} {...props} />
        </CopilotRepositoryProvider>
      </ValidationGroup>
    </div>
  );
};

export const TextAssetCollectionWithRecommendations = TextAssetCollectionWithRecommendationsInternal;

TextAssetCollectionWithRecommendations.propTypes = {
  isAudienceAd: PropTypes.bool,
  url: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  shortHeadline: PropTypes.string,
  shortHeadlineValidations: PropTypes.shape({
    display: PropTypes.bool,
    placeholder: PropTypes.string,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    isMultipleOptions: PropTypes.bool,
  }),
  serverContext: PropTypes.shape({
    CurrentLocale: PropTypes.string.isRequired,
  }).isRequired,
  disableCopilot: PropTypes.bool.isRequired,
  i18n: PropTypes.shape({ getString: func, getLocale: func }).isRequired,
  fetchRecommendationAssets: PropTypes.func.isRequired,
  cancelFetchRecommendationAssetsJob: PropTypes.func.isRequired,
  onChangeTexts: PropTypes.func.isRequired,
};

TextAssetCollectionWithRecommendations.defaultProps = {
  isAudienceAd: false,
  shortHeadline: '',
  shortHeadlineValidations: {
    placeholder: '',
    minLength: 0,
    maxLength: 30,
    display: true,
    isMultipleOptions: false,
  },
};
